import axiosInstance from '../utils/axiosInstance';
import {
    ApiResponse,
    ClusterSummaryPaginatedResponse,
    ClusterSummaryView,
    ClusterTimelineView,
    NewsArticle,
    NewsArticlePaginatedResponse,
    PaginatedResponse,
} from '../types/news';
import {FiltersResponse, NewsApiFilters, UnifiedCard} from '../types/cardData';

type UnifiedCardPaginatedResponse = PaginatedResponse<UnifiedCard>;

const NewsService = {
    getFiltersV2: async (): Promise<FiltersResponse> => {
        try {
            const response = await axiosInstance.get<ApiResponse<FiltersResponse>>('/v2/news/filters');
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch filter options');
        }
    },

    // ── V2 endpoints (unified card format) ───────────────────────────

    getNewsV2: async (page: number, pageSize: number, filters?: NewsApiFilters): Promise<ApiResponse<UnifiedCardPaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<UnifiedCardPaginatedResponse>>('/v2/news', {
                params: { page, pageSize, ...filters },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch news articles');
        }
    },

    getTrendingNewsV2: async (page: number, pageSize: number, filters?: NewsApiFilters): Promise<ApiResponse<UnifiedCardPaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<UnifiedCardPaginatedResponse>>('/v2/news/trending', {
                params: { page, pageSize, ...filters },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch trending news');
        }
    },

    searchNewsV2: async (query: string, page: number, pageSize: number): Promise<ApiResponse<UnifiedCardPaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<UnifiedCardPaginatedResponse>>('/v2/news/search/content', {
                params: { q: query, page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search news articles');
        }
    },

    searchNewsByTagsV2: async (tag: string, page: number, pageSize: number): Promise<ApiResponse<UnifiedCardPaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<UnifiedCardPaginatedResponse>>('/v2/news/search/tags', {
                params: { tag, page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search news articles by tag');
        }
    },

    getBreakingNewsV2: async (filters?: NewsApiFilters): Promise<ApiResponse<UnifiedCard[]>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<UnifiedCard[]>>('/v2/news/breaking', {
                params: filters,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch breaking news');
        }
    },

    getBreakingNewsPaginatedV2: async (page: number, pageSize: number, filters?: NewsApiFilters): Promise<ApiResponse<UnifiedCardPaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<UnifiedCardPaginatedResponse>>('/v2/news/breaking/paginated', {
                params: { page, pageSize, ...filters },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch paginated breaking news');
        }
    },

    getSimilarClustersV2: async (clusterId: number, limit: number = 8): Promise<ApiResponse<UnifiedCard[]>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<UnifiedCard[]>>(`/v2/news/cluster/${clusterId}/similar`, {
                params: { limit },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch similar clusters');
        }
    },

    // ── V1 endpoints (legacy — used by mobile app and pages not yet migrated) ──

    getNews: async (page: number, pageSize: number): Promise<ApiResponse<NewsArticlePaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<NewsArticlePaginatedResponse>>('/news', {
                params: { page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch news articles');
        }
    },

    getNewsById: async (id: string): Promise<ApiResponse<NewsArticle>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<NewsArticle>>(`/news/${id}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch news article');
        }
    },

    getTrendingNews: async (page: number, pageSize: number): Promise<ApiResponse<NewsArticlePaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<NewsArticlePaginatedResponse>>('/news/hot', {
                params: { page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch trending news');
        }
    },

    getTrendingSummaries: async (page: number, pageSize: number): Promise<ApiResponse<ClusterSummaryPaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<ClusterSummaryPaginatedResponse>>('/news/hot/summary', {
                params: { page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch trending summaries');
        }
    },

    searchNews: async (query: string, page: number, pageSize: number): Promise<ApiResponse<NewsArticlePaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<NewsArticlePaginatedResponse>>('/news/search/content', {
                params: { q: query, page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search news articles');
        }
    },

    searchNewsByTags: async (tag: string, page: number, pageSize: number): Promise<ApiResponse<NewsArticlePaginatedResponse>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<NewsArticlePaginatedResponse>>('/news/search/tags', {
                params: { tag, page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search news articles by tag');
        }
    },

    getClusterArticles: async (clusterId: number): Promise<ApiResponse<NewsArticle[]>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<NewsArticle[]>>(`/news/cluster/${clusterId}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch cluster articles');
        }
    },

    getClusterSummary: async (clusterId: number): Promise<ApiResponse<ClusterSummaryView>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<ClusterSummaryView>>(`/news/cluster/${clusterId}/summary`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch cluster summary');
        }
    },

    getClusterTimeline: async (clusterId: number): Promise<ApiResponse<ClusterTimelineView>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<ClusterTimelineView>>(`/news/cluster/${clusterId}/timeline`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch cluster timeline');
        }
    },

    getBreakingNews: async (): Promise<ApiResponse<ClusterSummaryView[]>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<ClusterSummaryView[]>>('/news/breaking');
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch breaking news');
        }
    },

    getSimilarClusters: async (clusterId: number, limit: number = 8): Promise<ApiResponse<ClusterSummaryView[]>> => {
        try {
            const response = await axiosInstance.get<ApiResponse<ClusterSummaryView[]>>(`/news/cluster/${clusterId}/similar`, {
                params: { limit },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch similar clusters');
        }
    },
};

export default NewsService;
